import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Checkbox,
  CopyButton,
  Group,
  PasswordInput,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconCopy, IconRefresh } from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

const ALL_EVENTS: Array<{ value: string; label: string; description: string }> = [
  {
    value: 'message.received',
    label: 'Message received',
    description: 'Inbound WhatsApp / Facebook / Instagram message from a contact.',
  },
  {
    value: 'message.sent',
    label: 'Message sent',
    description: 'Outbound message you (or your bot) sent successfully.',
  },
  {
    value: 'message.status',
    label: 'Message status',
    description: 'Delivery / read / failed status updates from Meta.',
  },
  {
    value: 'contact.created',
    label: 'Contact created',
    description: 'A new contact was added (manually or via inbound message).',
  },
  {
    value: 'bot_reply.fired',
    label: 'Bot reply fired',
    description: 'A keyword bot reply was matched and sent.',
  },
];

export function OutgoingWebhookPanel() {
  const cfg = trpc.tenancy.outgoingWebhook.getConfig.useQuery();
  const utils = trpc.useUtils();
  const setCfg = trpc.tenancy.outgoingWebhook.setConfig.useMutation({
    onSuccess: () => {
      void utils.tenancy.outgoingWebhook.getConfig.invalidate();
      notifications.show({ message: 'Saved', color: 'green' });
      setSecret('');
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const test = trpc.tenancy.outgoingWebhook.testFire.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: r.attempted
          ? r.ok
            ? `Receiver responded ${r.status}`
            : `Failed: ${r.reason ?? 'non-2xx'}`
          : `Skipped: ${r.reason ?? 'unknown'}`,
        color: r.ok ? 'green' : 'orange',
      });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const regen = trpc.tenancy.outgoingWebhook.regenerateSecret.useMutation({
    onSuccess: ({ secret: s }) => {
      void utils.tenancy.outgoingWebhook.getConfig.invalidate();
      // Match the verify-token UX: drop the cleartext into the field and
      // reveal it; it auto-masks again after the first copy.
      setSecret(s);
      setShowSecret(true);
      setSecretFresh(true);
      notifications.show({ message: 'Signing secret rotated', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const [enabled, setEnabled] = useState(false);
  const [endpoint, setEndpoint] = useState('');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [showSecret, setShowSecret] = useState(false);
  const [secretFresh, setSecretFresh] = useState(false);

  useEffect(() => {
    if (cfg.data) {
      setEnabled(cfg.data.enabled);
      setEndpoint(cfg.data.endpoint ?? '');
      // Store the saved secret (cleartext from server — settings-service
      // decrypts on read). Keep it masked by default; user clicks the eye
      // to reveal. Same pattern as the .NET SettingsApiAccess.razor page.
      setSecret(cfg.data.secret ?? '');
      setEvents(cfg.data.events ?? []);
      setShowSecret(false);
      setSecretFresh(false);
    }
  }, [cfg.data]);

  if (cfg.isPending) return <Skeleton height={400} />;

  return (
    <Stack gap="md">
      <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
        We POST a JSON payload to your endpoint when subscribed events fire. Each request is signed
        with HMAC-SHA256 in <code>X-LeadSync-Signature</code> and tagged with{' '}
        <code>X-LeadSync-Event</code>.
      </Alert>

      <Card>
        <Stack gap="sm">
          <Title order={5}>Endpoint</Title>
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
            label="Enabled"
          />
          <TextInput
            label="Endpoint URL"
            placeholder="https://hooks.example.com/leadsync"
            value={endpoint}
            onChange={(e) => setEndpoint(e.currentTarget.value)}
          />
          {enabled && endpoint.trim() && (
            <Text size="xs" c="dimmed">
              Forwarding is on. Check the <b>Delivery log</b> tab to confirm your receiver is
              accepting events — failed forwards show there.
            </Text>
          )}
          <Group align="flex-end" wrap="nowrap">
            <PasswordInput
              flex={1}
              label="Signing secret"
              description={
                secretFresh
                  ? 'New secret — copy it now. It will mask after copy.'
                  : secret
                    ? 'Stored encrypted. Click the eye to reveal, or type to set a new one.'
                    : 'No secret yet — Generate one or type a custom value.'
              }
              autoComplete="off"
              value={secret}
              onChange={(e) => {
                setSecret(e.currentTarget.value);
                setSecretFresh(false);
              }}
              visible={showSecret}
              onVisibilityChange={setShowSecret}
            />
            {secret && (
              <CopyButton value={secret}>
                {({ copy, copied }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy secret'}>
                    <ActionIcon
                      variant={secretFresh ? 'filled' : 'default'}
                      color={secretFresh ? 'green' : undefined}
                      size="lg"
                      onClick={() => {
                        copy();
                        if (secretFresh) {
                          setSecretFresh(false);
                          setShowSecret(false);
                        }
                      }}
                      mb={2}
                    >
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            )}
            <Button
              variant="default"
              leftSection={<IconRefresh size={14} />}
              loading={regen.isPending}
              onClick={() => regen.mutate()}
              mb={2}
            >
              Generate
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card>
        <Stack gap="sm">
          <Title order={5}>Subscribed events</Title>
          <Text c="dimmed" size="sm">
            Tick the events you want delivered to your endpoint.
          </Text>
          <Stack gap="xs">
            {ALL_EVENTS.map((ev) => (
              <Checkbox
                key={ev.value}
                checked={events.includes(ev.value)}
                onChange={(e) => {
                  // Capture synchronously: React nullifies currentTarget
                  // before the functional updater runs.
                  const isChecked = e.currentTarget.checked;
                  setEvents((prev) =>
                    isChecked ? [...prev, ev.value] : prev.filter((x) => x !== ev.value),
                  );
                }}
                label={
                  <span>
                    <Text component="span" fw={500}>
                      {ev.label}
                    </Text>{' '}
                    <Text component="span" c="dimmed" size="xs">
                      <code>{ev.value}</code>
                    </Text>
                    <br />
                    <Text component="span" c="dimmed" size="xs">
                      {ev.description}
                    </Text>
                  </span>
                }
              />
            ))}
          </Stack>
        </Stack>
      </Card>

      <Group>
        <Button
          leftSection={<IconCheck size={16} />}
          loading={setCfg.isPending}
          onClick={() =>
            setCfg.mutate({
              enabled,
              endpoint: endpoint.trim() || null,
              secret: secret.length > 0 ? secret : undefined,
              events,
            })
          }
        >
          Save changes
        </Button>
        <Button
          variant="default"
          leftSection={<IconRefresh size={16} />}
          loading={test.isPending}
          onClick={() => test.mutate()}
        >
          Send test event
        </Button>
      </Group>
    </Stack>
  );
}
